// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import {
  ICertificationWebpageApiClaimResponse,
  ICertificationWebpageApiDetailResponse,
  ICertificationWebpageApiDownloadDocumentResponse,
  ICertificationWebpageApiNullResponse,
  ICertificationWebpageApiResponse,
} from 'interfaces/webpage/certification/certification-api.interface'
import {
  ICertificationWebpageCancelAttrs,
  ICertificationWebpageClaimAttrs,
  ICertificationWebpageDownloadDocumentAttrs,
  ICertificationWebpageGetAttrs,
  ICertificationWebpageGetDetailAttrs,
} from 'interfaces/webpage/certification/certification-view.interface'
import { ICertificationWebpage, ICertificationWebpageDetail } from 'interfaces/webpage/certification'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponseWebpage, withHelper_fileDownload } from 'models/extensions/with-helper'

// Constant
import { ASSESSMENT_PHASES, ASSESSMENT_STATUS, LOADING } from 'constant/shared.constant'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Types
const { optional, array, model, maybeNull, number, string, enumeration } = types

// Loading
const { BASE, DETAIL, ACTION } = LOADING

const TitleModel = optional(
  array(
    model('Title').props({
      title: maybeNull(string),
    }),
  ),
  [],
)

const SuggestedCertifications = optional(
  array(
    model('SuggestedCertification').props({
      certificationId: maybeNull(number),
      certificationName: maybeNull(string),
    }),
  ),
  [],
)

// Current Certification Model
const CurrentCertificationModel = model('CurrentCertification').props({
  certificationId: maybeNull(number),
  assessmentId: maybeNull(number),
  fieldName: maybeNull(string),
  certificationName: maybeNull(string),
  status: maybeNull(enumeration('Status', Object.values(ASSESSMENT_STATUS))),
  phase: maybeNull(enumeration('Phase', Object.values(ASSESSMENT_PHASES))),
})

// Certification Detail Model
const CertificationDetailModel = model('CertificationDetailModel').props({
  id: maybeNull(number),
  name: maybeNull(string),
  competencyDetails: TitleModel,
  howToRegister: TitleModel,
  suggestedCertifications: SuggestedCertifications,
  phase: maybeNull(enumeration('Phase', Object.values(ASSESSMENT_PHASES))),
  status: maybeNull(enumeration('Status', Object.values(ASSESSMENT_STATUS))),
  currentCertification: maybeNull(CurrentCertificationModel),
})

const CertificationWebpageStore = types
  .model('CertificationWebpage')
  .props({
    certificationWebpage_loading: withHelper_commonLoadingModel,
    certificationWebpage_webpage: withHelper_baseResponseWebpage(),
    certificationWebpage_detail: optional(CertificationDetailModel, {}),
  })
  .actions(self => ({
    certificationWebpage_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.certificationWebpage_loading, payload)

      detach(self.certificationWebpage_loading)
      self.certificationWebpage_loading = cast(map)
    },
    certificationWebpage_SET_CERTIFICATE_WEBPAGE: (payload: ICertificationWebpage): void => {
      self.certificationWebpage_webpage = cast(payload)
    },
    certificationWebpage_SET_DETAIL: (payload: ICertificationWebpageDetail): void => {
      self.certificationWebpage_detail = cast(payload)
    },
    certificationWebpage_CLEAR_CERTIFICATE_WEBPAGE: (): void => {
      self.certificationWebpage_webpage = cast({})
    },
    certificationWebpage_CLEAR_DETAIL: (): void => {
      self.certificationWebpage_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch certification webpage
     *
     * @param {ICertificationWebpageGetAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpage_fetchCertificationWebpage: flow(function* (payload: ICertificationWebpageGetAttrs) {
      self.certificationWebpage_SET_LOADING({ type: BASE, value: true })

      const response: ICertificationWebpageApiResponse =
        yield self.environment.certificationWebpageApi.certificationWebpage_getCertification(payload)

      // Do when response success
      if (response.ok) {
        self.certificationWebpage_SET_CERTIFICATE_WEBPAGE(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpage_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Fetch certification detail
     *
     * @param {ICertificationWebpageGetDetailAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpage_fetchDetail: flow(function* (payload: ICertificationWebpageGetDetailAttrs) {
      self.certificationWebpage_SET_LOADING({ type: DETAIL, value: true })

      const response: ICertificationWebpageApiDetailResponse =
        yield self.environment.certificationWebpageApi.certificationWebpage_getDetail(payload)

      // Do when response success
      if (response.ok) {
        self.certificationWebpage_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpage_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Claim certification
     *
     * @param {ICertificationWebpageClaimAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpage_claim: flow(function* (payload: ICertificationWebpageClaimAttrs) {
      self.certificationWebpage_SET_LOADING({ type: ACTION, value: true })

      const response: ICertificationWebpageApiClaimResponse =
        yield self.environment.certificationWebpageApi.certificationWebpage_claim(payload)

      // Do when response success
      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess(response.res.data.transactionAssessmentId)
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpage_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Cancel certification
     *
     * @param {ICertificationWebpageCancelAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpage_cancel: flow(function* (payload: ICertificationWebpageCancelAttrs) {
      self.certificationWebpage_SET_LOADING({ type: ACTION, value: true })

      const response: ICertificationWebpageApiNullResponse =
        yield self.environment.certificationWebpageApi.certificationWebpage_cancel(payload)

      // Do when response success
      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpage_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Download document
     *
     * @param {ICertificationWebpageDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpage_downloadDocument: flow(function* (payload: ICertificationWebpageDownloadDocumentAttrs) {
      const response: ICertificationWebpageApiDownloadDocumentResponse =
        yield self.environment.certificationWebpageApi.certificationWebpage_downloadDocument({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        withHelper_fileDownload(response.res, response.headers!)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }
    }),
  }))

export const createCertificationWebpageStoreDefaultModel = () => types.optional(CertificationWebpageStore, {})
